import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { Button } from "@/components/Interface/Button/Button";
import { FormField } from "@/components/Interface/FormFields/FormField/FormField";
import { PasswordField } from "@/components/Interface/FormFields/PasswordField/PasswordField";
import { Link } from "@/components/Interface/Link/Link";
import { FocusRing } from "@/components/Layout/BaseLayouts/Footer/FocusRing";
import { TwoFactorCodeModal } from "@/components/Portal/UserAccount/Security/TwoFactorCodeModal";
import { VerifyEmailModal } from "@/components/Welcome/VerifyEmailModal";
import { useAlert } from "@/hooks/useAlert";
import { useCustomerLogin } from "@/hooks/useCustomerLogin";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { FCC } from "@/types";
import { FORGOT_PASSWORD_URL } from "@/utils/apiUrls";
import { KEYBOARD_TAB_INDEX } from "@/utils/keyboardTabIndex";
import {
  createLoginErrorHandler,
  LOGIN_ERROR_MESSAGES,
  LoginFormFields,
} from "@/utils/loginErrorHandler";

import { MagicLinkEmailSent } from "./MagicLinkEmailSent";
import { MagicLinkRequestForm } from "./MagicLinkRequestForm";
import { WelcomeCard } from "./WelcomeCard";

type LoginView = "password" | "magic-link-request" | "magic-link-sent";

const headingTag = { 1: "h1", 2: "h2" } as const;

interface Props {
  joinTribeOnLogin?: () => void;
  /** Magic link login is only enabled on the main welcome screen. */
  enableMagicLinkLogin?: boolean;
  /** 2 on pages whose hero headline is already the h1. */
  headingLevel?: 1 | 2;
}

export const LogInForm: FCC<Props> = ({
  children,
  joinTribeOnLogin,
  enableMagicLinkLogin = false,
  headingLevel = 1,
}) => {
  const Heading = headingTag[headingLevel];
  const [loginView, setLoginView] = useState<LoginView>("password");
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors, isSubmitting },
    setError,
    watch,
  } = useForm<LoginFormFields>({
    defaultValues: {
      emailAddress: "",
      password: "",
    },
  });
  const { t } = useTranslation();
  const navigate = useNavigate();
  const showAlert = useAlert();
  const [forceEnableButton, setForceEnableButton] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const {
    isLoading,
    shouldShowTwoFactorModal,
    shouldShowVerifyEmailModal,
    twoFactorMethod,
    twoFactorGrantToken,
    loginAsCustomer,
    setIsLoading,
    setShouldShowTwoFactorModal,
    setShouldShowVerifyEmailModal,
  } = useCustomerLogin({
    onSuccess: () => {
      // If VerifyEmailModal is shown we let it handle the onSuccess case
      if (shouldShowVerifyEmailModal) {
        return;
      }

      // If TwoFactorModal is shown we let it handle the onSuccess case
      if (shouldShowTwoFactorModal) {
        return;
      }

      handleAuthSuccess();
    },
    onError: (error) => {
      // Create error handler with current 2FA modal state
      const errorHandler = createLoginErrorHandler(
        setError,
        t,
        showAlert,
        "emailAddress",
      );
      errorHandler(error);
    },
  });

  useEffect(() => {
    // If button is disabled, set a 10-second timer to force reenable it
    if (isLoading || isSubmitting) {
      setForceEnableButton(false);
      const timer = setTimeout(() => {
        setForceEnableButton(true);
      }, 10000);

      return () => clearTimeout(timer);
    } else {
      // Reset force enable when button is naturally enabled
      setForceEnableButton(false);
    }
  }, [isLoading, isSubmitting]);

  const onSubmit = async (data: LoginFormFields) => {
    setSubmitting(true);

    try {
      await loginAsCustomer({
        email: data.emailAddress,
        password: data.password,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const email = watch("emailAddress");
  const password = watch("password");

  const handleAuthSuccess = () => {
    if (joinTribeOnLogin) {
      setTimeout(() => {
        joinTribeOnLogin();
        navigate(portalRoutes.friendsWithBenefits.groupOverview);
      }, 3000);
    } else {
      navigate(portalRoutes.cockpit.base);
    }
  };

  const resetToPasswordLogin = () => setLoginView("password");

  if (enableMagicLinkLogin && loginView === "magic-link-request") {
    return (
      <MagicLinkRequestForm
        onBack={resetToPasswordLogin}
        onSuccess={() => setLoginView("magic-link-sent")}
      />
    );
  }

  if (enableMagicLinkLogin && loginView === "magic-link-sent") {
    return <MagicLinkEmailSent onBack={resetToPasswordLogin} />;
  }

  return (
    <>
      <WelcomeCard>
        <form
          className="flex w-full flex-col gap-6"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Heading className="text-headline-md font-semibold leading-sm text-foreground-primary">
            {t("translation:newWelcome.logInForm.submitButton")}
          </Heading>

          <div className="flex flex-col gap-6">
            <FormField<LoginFormFields>
              id="emailAddress"
              name="emailAddress"
              label={t("translation:Common.generalForm.fields.email.label")}
              type="email"
              autocomplete="email"
              register={register}
              errors={errors}
              options={{
                required: t(
                  "translation:Common.generalForm.validation.emailRequired",
                ),
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: t(
                    "translation:Common.generalForm.validation.emailNotValid",
                  ),
                },
              }}
            />

            <div className="flex flex-col gap-1">
              <PasswordField<LoginFormFields>
                id="password"
                name="password"
                label={t(
                  "translation:Common.generalForm.fields.password.label",
                )}
                autocomplete="current-password"
                register={register}
                errors={errors}
                options={{
                  required: t(
                    "translation:Common.generalForm.validation.passwordRequired",
                  ),
                }}
              />
              <a
                className="group relative mt-1 inline-block w-fit border-b border-solid border-foreground-primary text-body-xs font-semibold text-foreground-primary outline-none hover:-mb-px hover:border-b-2 focus-visible:-mb-px focus-visible:border-b-2"
                href={FORGOT_PASSWORD_URL.toString()}
                tabIndex={KEYBOARD_TAB_INDEX}
                target="_self"
                rel="noreferrer"
              >
                <FocusRing />
                {t("translation:newWelcome.logInForm.forgotPassword")}
              </a>
            </div>
          </div>

          {shouldShowVerifyEmailModal && (
            <VerifyEmailModal
              email={email}
              password={password}
              handleSuccess={() => {
                setShouldShowVerifyEmailModal(false);

                // Trigger the navigation that was being skipped by the onSuccess early return
                handleAuthSuccess();
              }}
              handleClose={() => {
                setShouldShowVerifyEmailModal(false);
                setSubmitting(false);
                setIsLoading(false);

                reset({
                  emailAddress: "",
                  password: "",
                });
              }}
            />
          )}

          {shouldShowTwoFactorModal && (
            <TwoFactorCodeModal
              variant="dark"
              userName={email}
              userPassword={password}
              setShowModal={setShouldShowTwoFactorModal}
              method={twoFactorMethod}
              successAction={() => handleAuthSuccess()}
              type="login"
              twoFactorGrantToken={twoFactorGrantToken}
              onAccountLocked={() => {
                const accountLockedMessage = t(
                  LOGIN_ERROR_MESSAGES.ACCOUNT_LOCKED,
                );

                setError("emailAddress", { message: accountLockedMessage });
                setError("password", { message: accountLockedMessage });
              }}
            />
          )}

          <div className="flex flex-col items-center gap-4">
            <Button
              variant="primary"
              type="submit"
              disabled={!forceEnableButton && (isLoading || submitting)}
            >
              {t("translation:newWelcome.logInForm.submitButton")}
            </Button>

            {enableMagicLinkLogin && (
              <Link
                data-testid="magic-link-login-trigger"
                type="button"
                onClick={() => setLoginView("magic-link-request")}
              >
                {t("translation:newWelcome.magicLink.linkOnLogin")}
              </Link>
            )}
          </div>
        </form>
      </WelcomeCard>

      <div className="flex justify-center py-3">{children}</div>
    </>
  );
};
